import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Edit, ArrowRight, ExternalLink, Code, Archive, Trash2, MoreHorizontal, ArchiveRestore } from "lucide-react";
import GlassCard from "@/components/ui/glass-card";
import { Form } from "@/types";
import { motion } from "framer-motion";
import { useState } from "react";
import { DeleteFormDialog, ArchiveFormDialog } from "@/components/forms/dialogs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

type FormCardProps = {
  form: Form;
  index: number;
};

const FormCard = ({ form, index }: FormCardProps) => {
  const navigate = useNavigate();
  const [formToDelete, setFormToDelete] = useState<Form | null>(null);
  const [formToArchive, setFormToArchive] = useState<Form | null>(null);

  // Format views properly with thousands separators
  const formattedViews = form.views ? form.views.toLocaleString() : "0";
  
  // Open a URL in a new tab
  const openInNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  // Handle successful delete/archive actions
  const handleRefreshForms = () => {
    // Add explicit query parameters for better feedback
    const action = formToArchive?.isArchived ? 'unarchived=true' : 'archived=true';
    
    // Use location.reload() with redirect after a small delay for better UX
    setTimeout(() => {
      // Redirect with a query parameter
      window.location.href = `/forms?${action}&t=${Date.now()}`;
    }, 300);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <GlassCard className="h-full" hover={true}>
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center">
                <h3 className="font-semibold text-lg">{form.name}</h3>
                {form.isPublished ? (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                    Published
                  </span>
                ) : (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                    Draft
                  </span>
                )}
                {form.isArchived && (
                  <Badge variant="outline" className="ml-2 bg-muted text-muted-foreground">
                    Archived
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                {form.description}
              </p>
            </div>
            <div className="flex space-x-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem onClick={() => navigate(`/forms/${form.id}`)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  
                  {form.isPublished && !form.isArchived && (
                    <DropdownMenuItem onClick={() => openInNewTab(`/embed-options/${form.id}`)}>
                      <Code className="h-4 w-4 mr-2" />
                      Embed
                    </DropdownMenuItem>
                  )}
                  
                  {form.isPublished && !form.isArchived && (
                    <DropdownMenuItem onClick={() => openInNewTab(`/form-preview/${form.id}`)}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={() => setFormToArchive(form)}
                    className="text-amber-600"
                  >
                    {form.isArchived ? (
                      <>
                        <ArchiveRestore className="h-4 w-4 mr-2" />
                        Unarchive
                      </>
                    ) : (
                      <>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </>
                    )}
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    onClick={() => setFormToDelete(form)}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              <span className="inline-flex items-center mr-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Created {new Date(form.createdAt).toLocaleDateString()}
              </span>
              {form.isPublished && !form.isArchived && (
                <span className="inline-flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  {formattedViews} views
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {form.isPublished && !form.isArchived && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => openInNewTab(`/form-preview/${form.id}`)}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-2" />
                    Preview
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => openInNewTab(`/embed-options/${form.id}`)}
                  >
                    <Code className="h-3.5 w-3.5 mr-2" />
                    Embed
                  </Button>
                </>
              )}
              {!form.isArchived && (
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="h-8 text-foreground hover:bg-accent/20" 
                  onClick={() => {
                    if (form.isPublished) {
                      navigate(`/leads?formId=${form.id}`);
                    } else {
                      navigate(`/forms/${form.id}`);
                    }
                  }}
                >
                  {form.isPublished ? (
                    <>
                      <span className="text-sm">Submissions</span>
                      <ArrowRight className="ml-2 h-3.5 w-3.5" />
                    </>
                  ) : (
                    "Publish"
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </GlassCard>
      
      {/* Dialog components for delete and archive actions */}
      {formToDelete && (
        <DeleteFormDialog 
          open={!!formToDelete}
          onOpenChange={() => setFormToDelete(null)}
          formId={formToDelete.id}
          formName={formToDelete.name}
          onDelete={handleRefreshForms}
        />
      )}
      
      {formToArchive && (
        <ArchiveFormDialog
          open={!!formToArchive}
          onOpenChange={() => setFormToArchive(null)}
          formId={formToArchive.id}
          formName={formToArchive.name}
          isArchived={!!formToArchive.isArchived}
          onArchiveChange={handleRefreshForms}
        />
      )}
    </motion.div>
  );
};

export default FormCard;
